import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { backupApi } from '../services/api'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import toast from 'react-hot-toast'
import {
  Database, HardDrive, RotateCcw, Download, Trash2,
  Clock, CheckCircle2, AlertTriangle, Loader2, Shield,
} from 'lucide-react'

interface BackupRecord {
  id: number
  tipo: string
  filename: string
  size_bytes: number
  size_human: string
  stato: string
  note: string | null
  creato_da: string
  created_at: string | null
}

export default function Backup() {
  const qc = useQueryClient()
  const [ripristinaModal, setRipristinaModal] = useState<{ id: number; tipo: string } | null>(null)
  const [eliminaId, setEliminaId] = useState<number | null>(null)

  const { data: backups = [], isLoading } = useQuery<BackupRecord[]>({
    queryKey: ['backups'],
    queryFn: () => backupApi.lista().then(r => r.data),
    refetchInterval: 15_000,
  })

  const eseguiMut = useMutation({
    mutationFn: (tipo: 'dati' | 'completo') => backupApi.esegui(tipo),
    onSuccess: res => {
      qc.invalidateQueries({ queryKey: ['backups'] })
      const d = res.data
      if (d.stato === 'completato') toast.success(`Backup completato — ${d.size}`)
      else toast.error(`Errore backup: ${d.note}`)
    },
    onError: () => toast.error('Errore durante il backup'),
  })

  const ripristinaMut = useMutation({
    mutationFn: ({ id, cosa }: { id: number; cosa: string }) =>
      backupApi.ripristina(id, cosa as 'dati' | 'file' | 'tutto'),
    onSuccess: res => {
      setRipristinaModal(null)
      qc.invalidateQueries({ queryKey: ['backups'] })
      if (res.data.stato === 'ok') toast.success('Ripristino completato con successo')
      else toast.error(`Ripristino parziale: ${res.data.errori?.join(', ')}`)
    },
    onError: () => toast.error('Errore durante il ripristino'),
  })

  const eliminaMut = useMutation({
    mutationFn: (id: number) => backupApi.elimina(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backups'] })
      setEliminaId(null)
      toast.success('Backup eliminato')
    },
  })

  const handleDownload = async (id: number, filename: string) => {
    try {
      const res = await backupApi.download(id)
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Errore download')
    }
  }

  const STATO_ICON: Record<string, JSX.Element> = {
    completato: <CheckCircle2 size={14} color="#16a34a" />,
    errore:     <AlertTriangle size={14} color="#dc2626" />,
    in_corso:   <Loader2 size={14} color="#2563eb" />,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={22} color="#2563eb" /> Backup & Ripristino
        </h1>
        <p className="page-subtitle">
          Backup automatico ogni notte alle 00:00 — Ripristino selettivo con un click
        </p>
      </div>

      {/* Azioni manuali */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#EFF6FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Database size={20} color="#2563eb" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Backup Dati</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, marginBottom: 12 }}>
                Esporta solo il database PostgreSQL (.sql). Veloce — ideale per backup frequenti.
              </div>
              <button
                onClick={() => eseguiMut.mutate('dati')}
                disabled={eseguiMut.isPending}
                className="btn-primary"
                style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {eseguiMut.isPending ? <Loader2 size={13} /> : <Database size={13} />}
                {eseguiMut.isPending ? 'In corso...' : 'Avvia Backup Dati'}
              </button>
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#FFF7ED',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HardDrive size={20} color="#ea580c" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Backup Completo</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, marginBottom: 12 }}>
                Database + file caricati (rapportini, allegati) + configurazione Docker (.tar.gz).
              </div>
              <button
                onClick={() => eseguiMut.mutate('completo')}
                disabled={eseguiMut.isPending}
                className="btn-secondary"
                style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                  borderColor: '#fed7aa', color: '#ea580c' }}
              >
                {eseguiMut.isPending ? <Loader2 size={13} /> : <HardDrive size={13} />}
                {eseguiMut.isPending ? 'In corso...' : 'Avvia Backup Completo'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Banner scheduler */}
      <div className="card" style={{ padding: '12px 16px', background: '#f0fdf4',
        borderColor: '#bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Clock size={15} color="#16a34a" />
        <span style={{ fontSize: 13, color: '#166534', fontWeight: 500 }}>
          Backup automatico attivo — ogni giorno alle 00:00 viene eseguito un backup dati automatico.
        </span>
      </div>

      {/* Lista backup */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <span style={{ fontWeight: 600, color: '#1e293b' }}>Storico Backup</span>
        </div>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: 48, color: '#94a3b8' }}>
            <Loader2 size={18} /> Caricamento...
          </div>
        ) : backups.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            Nessun backup disponibile. Esegui il primo backup manualmente.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  {['Data', 'Tipo', 'File', 'Dimensione', 'Stato', 'Creato da', 'Azioni'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Azioni' ? 'center' : 'left',
                      fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase',
                      letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 16px', color: '#475569', fontFamily: 'monospace', fontSize: 12 }}>
                      {b.created_at ? format(new Date(b.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                        background: b.tipo === 'completo' ? '#FFF7ED' : '#EFF6FF',
                        color: b.tipo === 'completo' ? '#ea580c' : '#2563eb' }}>
                        {b.tipo === 'completo' ? <HardDrive size={11} /> : <Database size={11} />}
                        {b.tipo}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#94a3b8', fontFamily: 'monospace',
                      fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }}>{b.filename}</td>
                    <td style={{ padding: '10px 16px', color: '#475569', fontSize: 12 }}>
                      {b.size_human || '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12,
                        color: b.stato === 'completato' ? '#16a34a' : b.stato === 'errore' ? '#dc2626' : '#2563eb' }}>
                        {STATO_ICON[b.stato]}
                        {b.stato}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#64748b', fontSize: 12 }}>{b.creato_da}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        {b.stato === 'completato' && (
                          <>
                            <button
                              onClick={() => setRipristinaModal({ id: b.id, tipo: b.tipo })}
                              title="Ripristina"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
                                borderRadius: 6, border: 'none', background: 'transparent',
                                color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                              <RotateCcw size={13} /> Ripristina
                            </button>
                            <button
                              onClick={() => handleDownload(b.id, b.filename)}
                              title="Scarica"
                              style={{ padding: '4px 8px', borderRadius: 6, border: 'none',
                                background: 'transparent', color: '#475569', cursor: 'pointer' }}>
                              <Download size={13} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEliminaId(b.id)}
                          title="Elimina"
                          style={{ padding: '4px 8px', borderRadius: 6, border: 'none',
                            background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Ripristina */}
      {ripristinaModal && (
        <div onClick={e => { if (e.target === e.currentTarget) setRipristinaModal(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 16,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF2F2',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RotateCcw size={20} color="#dc2626" />
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>Ripristina Backup</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Scegli cosa ripristinare</div>
              </div>
            </div>
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
              padding: '10px 14px', fontSize: 13, color: '#b91c1c', fontWeight: 500, marginBottom: 16 }}>
              ⚠ Il ripristino sovrascrive i dati attuali. Operazione irreversibile.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {[
                { cosa: 'dati',    label: 'Solo Database',
                  desc: 'Ripristina solo il database PostgreSQL', icon: <Database size={16} /> },
                ...(ripristinaModal.tipo === 'completo' ? [
                  { cosa: 'file',  label: 'Solo File',
                    desc: 'Ripristina solo i file caricati', icon: <HardDrive size={16} /> },
                  { cosa: 'tutto', label: 'Database + File',
                    desc: 'Ripristino totale del sistema', icon: <Shield size={16} /> },
                ] : []),
              ].map(opt => (
                <button key={opt.cosa}
                  onClick={() => ripristinaMut.mutate({ id: ripristinaModal.id, cosa: opt.cosa })}
                  disabled={ripristinaMut.isPending}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    borderRadius: 10, border: '1.5px solid #e5e7eb', background: 'white',
                    cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ color: '#2563eb', flexShrink: 0 }}>{opt.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{opt.desc}</div>
                  </div>
                  {ripristinaMut.isPending && <Loader2 size={14} color="#2563eb" style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
            </div>
            <button onClick={() => setRipristinaModal(null)} className="btn-secondary"
              style={{ width: '100%' }}>Annulla</button>
          </div>
        </div>
      )}

      {/* Modal Elimina */}
      {eliminaId !== null && (
        <div onClick={e => { if (e.target === e.currentTarget) setEliminaId(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 16,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="card" style={{ width: '100%', maxWidth: 360, padding: 24 }}>
            <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Elimina backup?</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>
              Il file verrà eliminato dal disco. Operazione irreversibile.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEliminaId(null)} className="btn-secondary" style={{ flex: 1 }}>
                Annulla
              </button>
              <button
                onClick={() => eliminaMut.mutate(eliminaId)}
                disabled={eliminaMut.isPending}
                style={{ flex: 1, padding: '8px 16px', border: 'none', borderRadius: 8,
                  background: '#dc2626', color: 'white', fontWeight: 600,
                  cursor: 'pointer', fontSize: 14 }}>
                {eliminaMut.isPending ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
